import { useState } from 'react'
import { GALLERY } from '../content/siteContent'

export default function GalleryGrid() {
  const [loaded, setLoaded] = useState<Record<number, boolean>>({})
  const [enlarged, setEnlarged] = useState<number | null>(null)

  const handleLoad = (i: number) => setLoaded((prev) => ({ ...prev, [i]: true }))

  return (
    <>
      {/* Grid */}
      <div
        className="columns-2 sm:columns-3 lg:columns-4 gap-2 sm:gap-3 md:gap-4 space-y-2 sm:space-y-3 md:space-y-4"
        role="list"
        aria-label="Photo gallery"
      >
        {GALLERY.images.map((img, i) => (
          <div
            key={img.src}
            className="break-inside-avoid"
            role="listitem"
          >
            <button
              onClick={() => setEnlarged(i)}
              className="w-full rounded-xl2 overflow-hidden shadow-card hover:shadow-lift hover:scale-[1.02] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-sky focus:ring-offset-2 block"
              aria-label={`View larger: ${img.alt}`}
            >
              {!loaded[i] && (
                <div className="bg-brand-cream aspect-square animate-pulse rounded-xl2" aria-hidden="true" />
              )}
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                onLoad={() => handleLoad(i)}
                // NOTE: Current images are low-res thumbnails (~150-300px).
                // Replace with full-resolution originals for best quality.
                className={`w-full object-cover transition-opacity duration-500 ${
                  loaded[i] ? 'opacity-100' : 'opacity-0 absolute'
                }`}
                style={{ imageRendering: 'auto' }}
              />
            </button>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {enlarged !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          onClick={() => setEnlarged(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={GALLERY.images[enlarged].src}
              alt={GALLERY.images[enlarged].alt}
              className="w-full max-h-[85vh] object-contain rounded-xl2 shadow-lift"
            />
            <button
              onClick={() => setEnlarged(null)}
              className="absolute top-3 right-3 bg-white/90 hover:bg-white text-brand-navy rounded-full w-9 h-9 flex items-center justify-center shadow-card transition-colors focus:outline-none focus:ring-2 focus:ring-brand-sky"
              aria-label="Close image viewer"
            >
              ✕
            </button>
            {/* Prev / Next */}
            <button
              onClick={() => setEnlarged((e) => ((e ?? 0) - 1 + GALLERY.images.length) % GALLERY.images.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-brand-navy rounded-full w-9 h-9 flex items-center justify-center shadow-card transition-colors focus:outline-none focus:ring-2 focus:ring-brand-sky"
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              onClick={() => setEnlarged((e) => ((e ?? 0) + 1) % GALLERY.images.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-brand-navy rounded-full w-9 h-9 flex items-center justify-center shadow-card transition-colors focus:outline-none focus:ring-2 focus:ring-brand-sky"
              aria-label="Next image"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  )
}
